import React, { useContext } from 'react';
import { compose } from 'recompose';
import {
    Table,
    Tabs,
    Tab,
    Typography,
    TableHead,
    TableContainer,
    TablePagination,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Button,
    TextField,
    Checkbox,
} from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { css } from '@emotion/react';
import withAdminRights from '../../Auth/withAdminRights';
import { UserContext } from '../../App';
import { COLUMNS } from './constants';
import { OnwardChip } from '@/styles/blocks';
import { useAdminUserActions, useAdminUsers } from './hooks';

const AdminUsers = () => {
    const { sidebarCollapsed } = useContext(UserContext);
    const {
        tab,
        changeTab,
        visibleTabs,
        page,
        setPage,
        searchInput,
        setSearchInput,
        setSelectedAirports,
        airportOptions,
        filteredUsers,
        loading,
        updateClient,
        updateLoading,
    } = useAdminUsers();
    const { logInAsUser, logInAsOrg, goToUser } = useAdminUserActions();

    const colFilter = (column) => !column.tabs || column.tabs.includes(tab.header);
    const isOrgTab = tab.data === 'orgAdmins';
    const hasDetails = tab.header !== 'Teammates' && !isOrgTab;
    const rowKey = (entity) => (isOrgTab ? entity.organization_id : entity.user.user_id);

    return (
        <div
            style={{ margin: '16px' }}
            className={`${sidebarCollapsed && 'collapse-margin'} sidenav-margin-responsive`}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ fontWeight: 600 }} variant="h4">
                    Super Admin
                </Typography>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {tab.data === 'carriers' && (
                        <Autocomplete
                            size="small"
                            css={css`
                                margin-right: 15px;
                                width: 350px;
                            `}
                            multiple
                            disableCloseOnSelect
                            value={airportOptions.filter((airport) => airport.selected)}
                            options={airportOptions}
                            getOptionSelected={(option, value) => {
                                return option.value === value.value;
                            }}
                            getOptionLabel={(option) => option.label}
                            renderTags={(options, getTagProps) => {
                                return options.map((option, index) => (
                                    <OnwardChip
                                        key={option.value}
                                        label={option.label}
                                        {...getTagProps({ index })}
                                        {...(option.disabled ? { onDelete: null } : {})}
                                        css={css`
                                            color: ${option.disabled ? '#8d8d8d' : 'black'};
                                        `}
                                    />
                                ));
                            }}
                            renderOption={(option, { selected }) => {
                                return (
                                    <li key={option.value}>
                                        <Checkbox color="primary" checked={selected} />
                                        {option.label}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Airport Codes"
                                    onKeyDown={(e) => {
                                        if (['Backspace'].includes(e.key)) {
                                            e.stopPropagation();
                                        }
                                    }}
                                />
                            )}
                            onChange={(e, value = []) => {
                                const _selectedAirports = value.map((option) => option.label);
                                setSelectedAirports(_selectedAirports);
                            }}
                        />
                    )}

                    <TextField
                        size="small"
                        variant="outlined"
                        color="primary"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search..."
                    />
                </div>
            </div>

            <Tabs textColor="primary" indicatorColor="primary" value={tab.data} onChange={changeTab}>
                {visibleTabs.map((tab) => (
                    <Tab label={tab.header} value={tab.data} key={tab.header} />
                ))}
            </Tabs>

            <Paper>
                {/* Scrolls sideways once every column is at its minimum width, instead of spilling out of the card */}
                <TableContainer>
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                {COLUMNS.filter(colFilter).map((column) => (
                                    <TableCell key={column.label} align="center">
                                        {column.label}
                                    </TableCell>
                                ))}
                                <TableCell />
                                {hasDetails && <TableCell />}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading
                                ? [...Array(5)].map((...[, i]) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={COLUMNS.filter(colFilter).length + 1}>
                                              <Skeleton variant="text" />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : filteredUsers.slice(page * 10, page * 10 + 10).map((entity) => (
                                      <TableRow key={rowKey(entity)}>
                                          {COLUMNS.filter(colFilter).map((column) => (
                                              <TableCell align="center" key={`${rowKey(entity)}-${column.label}`}>
                                                  {column.value(entity, loading || updateLoading, {
                                                      updateClient: (client_id, update = {}) =>
                                                          updateClient({ variables: { client_id, update } }),
                                                  })}
                                              </TableCell>
                                          ))}
                                          <TableCell align="center">
                                              <Button
                                                  variant="contained"
                                                  style={{ color: '#eee' }}
                                                  color="primary"
                                                  onClick={() => (isOrgTab ? logInAsOrg(entity) : logInAsUser(entity))}
                                              >
                                                  Log&nbsp;In
                                              </Button>
                                          </TableCell>
                                          {hasDetails && (
                                              <TableCell align="center">
                                                  <Button
                                                      variant="contained"
                                                      style={{ color: '#eee' }}
                                                      color="primary"
                                                      onClick={() => goToUser(entity)}
                                                  >
                                                      Details
                                                  </Button>
                                              </TableCell>
                                          )}
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredUsers.length}
                    page={page}
                    rowsPerPage={10}
                    rowsPerPageOptions={[]}
                    onPageChange={(...[, newPage]) => setPage(newPage)}
                />
            </Paper>
        </div>
    );
};

export default React.memo(compose(withAdminRights)(AdminUsers));
